import { Box } from "@mui/material";
import { AxiosError } from "axios";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as constants from "../../../constants";
import ContainedButton from "../../../shared/Button/CustomContainedButton";
import OutlinedButton from "../../../shared/Button/customOutlinedButton";
import CustomTextField from "../../../shared/CustomTextFeild";
import ReusableTable, { Column } from "../../../shared/ReusableTable/ReusableTable";
import { resetImportDataDetails } from "../../../store/Financials/importDataSlice";
import { resetSubChartDetails } from "../../../store/Financials/saveChartSlice";
import { RootState } from "../../../store/store";
import { resetTableState, setPagination, setTableFilters } from "../../../store/tableSlice";
import basestyle from "../../CSS/Base.module.css";
import { getActualInfoList, getActualSearchCount, getAdminActualInfoList, getAdminActualSearchCount } from "../services/Financials/actuals.service";
import { SectionTitle, StyledOneChildContainer, StyledParentContainer, StyledTwoChildContainer } from "../StyledComponents/Container";
import { formatDate } from "../Utils/DataFormat";
import { formatCurrency } from "../Utils/formatUtils";
import { setOpenMenuIndex, setActiveIndex } from "../../../store/MenutItems/menuUiSlice";


const ActualsListing = () => {
    const [count, setCount] = useState(0);
    const [actualList, setActualList] = useState([]);

    const participantDetail = useSelector((state: RootState) => state.participantDetails);
    const [participantType, setParticipantType] = useState("");
    const retain = useSelector((state: RootState) => state.table.actual);
    const [budgetYear, setBudgetYear] = useState(retain?.filters?.budgetYear ?? "");
    const [page, setPage] = useState(retain?.page || 0);
    const [rowsPerPage, setRowsPerPage] = useState(retain?.pageSize || 5);
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const userDetails = useSelector((state: RootState) => state.auth);
    const AdminProfile = useSelector((state: RootState) => state.participantAdminProfile);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const translate = t as (key: string) => string;
    const adminColumns: Column[] = [
        { id: "forecastYear", label: translate('budgetYear'), minWidth: 150, align: "center", isHyperlink: false },
        { id: "income", label: translate('totalIncome'), minWidth: 150, align: "right", isHyperlink: false },
        { id: "expenses", label: translate('totalExpenses'), minWidth: 150, align: "right", isHyperlink: false },
    ];

    const nonAdminColumns: Column[] = [
        { id: "forecastYear", label: translate('budgetYear'), minWidth: 150, align: "center", isHyperlink: true },
        { id: "income", label: translate('totalIncome'), minWidth: 150, align: "right", isHyperlink: false },
        { id: "expenses", label: translate('totalExpenses'), minWidth: 150, align: "right", isHyperlink: false },
        { id: "completionDate", label: translate('currentBudgetCompletionDate'), minWidth: 200, align: "center", isHyperlink: false },
        { id: "financialReport", label: translate('financialReport'), minWidth: 200, align: "center", isHyperlink: true },
    ];

    const handleKeyPress = (event: any) => {
        if (event.key === 'Enter') {
            handleSearch();
        }
    };

    const handleSearch = () => {
        setPage(0);
        if (AdminProfile.participantAdminProfile === constants.ADMIN_VIEW) {
            getAdminActualInfo(budgetYear, 0, rowsPerPage);
        } else {
            getActualInfo(budgetYear, 0, rowsPerPage);
        }
    };

    const handleClear = () => {
        setBudgetYear("");
        setPage(0);
        setRowsPerPage(5);
        if (AdminProfile.participantAdminProfile === constants.ADMIN_VIEW) {
            getAdminActualInfo("", 0, 5);
        } else {
            getActualInfo("", 0, 5);
        }
        dispatch(resetTableState({ tableKey: 'actual' }));
    };
    const handleImportActual = () => {
        dispatch(resetImportDataDetails());
        navigate("/actualsImport", {
            state: {
                listingPageType: AdminProfile.participantAdminProfile === constants.ADMIN_VIEW ? "adminActualListing" : "actualListing",
            },
        });
        dispatch(resetSubChartDetails());
    };
    useEffect(() => {
        if (userDetails?.defaultParticipantId) {
            const participant = (participantDetail.participantsArray as { participantId: string; participantType: string }[]).find((item) => item.participantId === userDetails.defaultParticipantId);
            setParticipantType(participant?.participantType ?? "")

        }
    }, [userDetails?.defaultParticipantId]);



    useEffect(() => {
        if (AdminProfile.participantAdminProfile === constants.ADMIN_VIEW) {
            getAdminActualInfo(budgetYear, page, rowsPerPage);
        } else {
            getActualInfo(budgetYear, page, rowsPerPage);
        }
    }, [page, rowsPerPage, AdminProfile]);

    async function getActualInfo(budgetYear: string, pages: number, rowsPerPage: number) {
        setIsLoading(true);
        try {
            const actualSearchVO = {
                participantId: userDetails.defaultParticipantId,
                forecastYear: budgetYear,
                pageNo: -1,
                pageSize: -1
            };
            const responseCount = await getActualSearchCount(actualSearchVO);
            if (responseCount?.status === constants.STATUS_CODES.SUCCESS) {
                const count = responseCount?.data?.data;
                if (count > 0) {
                    setCount(count);
                    const updatedActualSearchVO = {
                        participantId: userDetails.defaultParticipantId,
                        forecastYear: budgetYear,
                        pageNo: pages + 1,
                        pageSize: rowsPerPage
                    };

                    dispatch(setTableFilters({ tableKey: 'actual', filters: { budgetYear } }));
                    dispatch(setPagination({ tableKey: 'actual', page: page, pageSize: rowsPerPage }));

                    const responseList = await getActualInfoList(updatedActualSearchVO);
                    if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
                        const budgetActualSearchVOList = responseList?.data?.data?.budgetActualSearchVOList;
                        if (budgetActualSearchVOList?.length > 0) {
                            setActualList(budgetActualSearchVOList);
                        } else {
                            setActualList([]);
                        }
                    } else {
                        setActualList([]);
                    }
                } else {
                    setActualList([]);
                    setCount(0);
                }
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data?.message ?? error.message);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        } finally {
            setIsLoading(false);
        }
    }
    async function getAdminActualInfo(budgetYear: string, pages: number, rowsPerPage: number) {
        setIsLoading(true);
        try {
            const actualSearchVO = {
                forecastYear: budgetYear,
                pageNo: -1,
                pageSize: -1
            };

            const responseCount = await getAdminActualSearchCount(actualSearchVO);
            if (responseCount?.status === constants.STATUS_CODES.SUCCESS) {
                const count = responseCount?.data?.data;
                if (count > 0) {
                    setCount(count);
                    const updatedActualSearchVO = {
                        forecastYear: budgetYear,
                        pageNo: pages + 1,
                        pageSize: rowsPerPage
                    };
                    dispatch(setTableFilters({ tableKey: 'actual', filters: { budgetYear } }));
                    dispatch(setPagination({ tableKey: 'actual', page: page, pageSize: rowsPerPage }));

                    const responseList = await getAdminActualInfoList(updatedActualSearchVO);
                    if (responseList?.status === constants.STATUS_CODES.SUCCESS) {
                        const budgetActualSearchVOList = responseList?.data?.data?.budgetActualSearchVOList;
                        if (budgetActualSearchVOList?.length > 0) {
                            setActualList(budgetActualSearchVOList);
                        } else {
                            setActualList([]);
                        }
                    } else {
                        setActualList([]);
                    }
                } else {
                    setActualList([]);
                    setCount(0);
                }
            }
        } catch (error) {
            if (error instanceof AxiosError) {
                console.error(translate('axiosError'), error.response?.data?.message ?? error.message);
            } else {
                console.error(translate('unexpectedError'), error);
            }
        } finally {
            setIsLoading(false);
        }
    }

    const transformedActualList = actualList.map((item: any) => ({
        ...item,
        expenses: item.expenses ? formatCurrency(item.expenses) : "$0.00",
        income: item.income ? formatCurrency(item.income) : "$0.00",
        completionDate: item.completionDate ? formatDate(item.completionDate) : "-",
    }));

    const handleActualYearClick = (rowData: any) => {
        navigate("/actualsDetails", {
            state: {
                forecastYear: rowData.forecastYear,
            },
        });
    };

    const handleBack = () => { 
        dispatch(setActiveIndex(0));
        dispatch(setOpenMenuIndex(null));
        if (AdminProfile.participantAdminProfile === constants.ADMIN_VIEW) {
            navigate("/rcboSummary");
        } else {
            navigate("/summary");
        }
    };

    return (
        <StyledParentContainer>
            <StyledOneChildContainer>
                <StyledTwoChildContainer sx={{ border: "none" }}>
                    <SectionTitle>{translate('actuals')}</SectionTitle>
                    <Box sx={{ width: "100%", px: 1.5 }}>
                        <div className="search-fields-container">
                            <div className="left-fields">
                                <CustomTextField
                                    label={translate('budgetYear')}
                                    onKeyDown={handleKeyPress}
                                    value={budgetYear}
                                    onChange={(e) => {
                                        const value = e.target.value.replace(/\s/g, '');
                                        setBudgetYear(value);
                                    }}
                                    className="small-text-field"
                                    size="small"
                                    inputProps={{ maxLength: 10 }}
                                />
                                <ContainedButton onClick={handleSearch}>{translate('search')}</ContainedButton>
                                <OutlinedButton onClick={handleClear}>{translate('clear')}</OutlinedButton>
                            </div>
                            <div className="right-fields">
                                <ContainedButton onClick={handleImportActual}>  {translate('importActuals')} </ContainedButton>
                            </div>
                        </div>
                        <ReusableTable
                            tableKey="actualListing"
                            columns={AdminProfile.participantAdminProfile === constants.ADMIN_VIEW ? adminColumns : nonAdminColumns}
                            rows={transformedActualList}
                            count={count}
                            page={page}
                            participantType={participantType}
                            rowsPerPage={rowsPerPage}
                            onPageChange={(newPage) => setPage(newPage)}
                            onRowsPerPageChange={(newRowsPerPage) => setRowsPerPage(newRowsPerPage)}
                            loading={isLoading}
                            onRowClick={handleActualYearClick}
                        />
                        <Box className={basestyle.buttoncontainer}>
                            <ContainedButton onClick={handleBack}>  {translate('back')}  </ContainedButton>
                        </Box>
                    </Box>
                </StyledTwoChildContainer>
            </StyledOneChildContainer>
        </StyledParentContainer>
    );
};

export default ActualsListing;